import { API_ENDPOINTS } from '@/config/api.config';
import apiClient from '@/services/api';
import { BulkImportHistory, BulkImportRecord, BulkImportStatus, userManagementV2Service, UserV2, UserV2CreateRequest, UserV2Metrics } from '@/services/userManagementV2.service';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { FileUploadField } from '@/components/common/form-fields';
import { AlertCircle, Award, Book, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, Clock, Download, Edit2, Eye, EyeOff, FileText, Filter, History, Key, Lock, Minus, Plus, RefreshCw, Save, Search, Trash2, Upload, UserPlus, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { Select } from '@/components/ui/Select';
import { UserFormModal, UserStatusBadge } from '@/components/users';
import { toast } from '@/components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { USERS_PERMISSION } from '@/constants/constant-permissions';
import { TableSkeleton } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import { exportService } from '@/services/export.service';
import { generateRequestId, encryptAES } from '@/utils/crypto';

interface GroupDropdownItem {
  roles: Role[];
  _id: string;
  group_key: string | null;  // API returns null for group_key, we'll use _id
  group_name: string;
}

interface Role {
  _id: string;
  role_key: string;
  role_name: string;
}

interface FilterFormData {
  search: string;
  department: string;
  status: string;
}

export default function Users() {
  usePermissionRouteAccess([
    USERS_PERMISSION.ADMIN_USERS_READ,
    USERS_PERMISSION.ADMIN_USERS_WRITE,
  ]);
  const canWrite = useHasPermission([USERS_PERMISSION.ADMIN_USERS_WRITE]);

  const navigate = useNavigate();
  const [users, setUsers] = useState<UserV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [totalUsers, setTotalUsers] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserV2 | null>(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{id: string, name: string} | null>(null);

  // Metrics state
  const [metrics, setMetrics] = useState<UserV2Metrics | null>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [bulkImportStatus, setBulkImportStatus] = useState<BulkImportStatus | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [processingImport, setProcessingImport] = useState(false);
  const [duplicateHandling, setDuplicateHandling] = useState<'SKIP' | 'UPDATE' | 'ERROR'>('SKIP');

  // Bulk import history state
  const [showBulkDropdown, setShowBulkDropdown] = useState(false);
  const [showImportHistoryModal, setShowImportHistoryModal] = useState(false);
  const [importHistory, setImportHistory] = useState<BulkImportHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<BulkImportHistory | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const bulkDropdownRef = useRef<HTMLDivElement>(null);

  // Bulk import record details state
  const [showRecordDetailsModal, setShowRecordDetailsModal] = useState(false);
  const [importRecords, setImportRecords] = useState<BulkImportRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsLimit] = useState(20);
  const [recordsTotalPages, setRecordsTotalPages] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsFilter, setRecordsFilter] = useState<'ALL' | 'PENDING' | 'SUCCESS' | 'FAILED' | 'SKIPPED'>('ALL');
  const [recordsSearch, setRecordsSearch] = useState('');

  // Export state
  const [exportingUsers, setExportingUsers] = useState(false);

  // Reset password state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<UserV2 | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);

  // Filters with react-hook-form
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: {
      search: '',
      department: 'all',
      status: 'all',
    },
  });

  const searchQuery = watch('search');
  const filterDepartment = watch('department');
  const filterStatus = watch('status');

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, DEFAULT_DEBOUNCE_DELAY);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // Available groups and roles from API
  const [availableGroups, setAvailableGroups] = useState<GroupDropdownItem[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Add User Modal Form State
  const [formData, setFormData] = useState<UserV2CreateRequest>({
    email: '',
    given_name: '',
    family_name: '',
    password:'',
    personal_email: '',
    employee_id: '',
    mobile_number: '',
    profile_image: '',
    groups: [{ group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] }], // Start with one group and one role as both are required
    meta_data: {},
    linked_social_accounts: []
  });

  // Metadata fields state for dynamic key-value pairs
  const [metaFields, setMetaFields] = useState([{ key: '', value: '' }]);

  useEffect(() => {
    loadGroupsAndRoles();
    loadMetrics();
  }, []);

  // Reset to page 1 when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, filterDepartment, filterStatus]);

  useEffect(() => {
    loadUsers();
  }, [currentPage, itemsPerPage, debouncedSearchQuery, filterDepartment, filterStatus]);

  // Handle click outside for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkDropdownRef.current && !bulkDropdownRef.current.contains(event.target as Node)) {
        setShowBulkDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadGroupsAndRoles = async () => {
    try {
      setLoadingOptions(true);

      // Load groups dropdown (includes roles within each group)
      const groupsResponse = await apiClient.get(API_ENDPOINTS.GROUPS.DROPDOWN);
      if (groupsResponse.data.success && groupsResponse.data.data) {
        const validGroups = groupsResponse.data.data.filter((g: GroupDropdownItem) => g.group_key !== null);
        setAvailableGroups(validGroups);
      }

    } catch (error) {
      console.error('Error loading groups and roles:', error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadMetrics = async () => {
    try {
      setLoadingMetrics(true);
      const response = await userManagementV2Service.getUserMetrics();
      setMetrics(response.data);
    } catch (error: any) {
      console.error('Error loading metrics:', error);
      // Don't set error for metrics as it's not critical for the page functionality
    } finally {
      setLoadingMetrics(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params: any = {
        page: currentPage,
        limit: itemsPerPage
      };

      // Add search query if present (using debounced value)
      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }

      // Add department filter if not 'all'
      if (filterDepartment !== 'all') {
        params.group_key = filterDepartment;
      }

      // Add status filter if not 'all'
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await userManagementV2Service.getUsersList(params);
      setUsers(response.data.data);
      setTotalUsers(response.data.total);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
    } catch (error: any) {
      setError(error.message || 'Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      given_name: '',
      family_name: '',
      password:'',
      personal_email: '',
      employee_id: '',
      mobile_number: '',
      profile_image: '',
      groups: [{ group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] }], // Start with one group and one role as both are required
      meta_data: {},
      linked_social_accounts: []
    });
    setMetaFields([{ key: '', value: '' }]);
    setFieldErrors({});
    setError(null);
  };

  const handleAddMetaField = () => {
    setMetaFields([...metaFields, { key: '', value: '' }]);
  };

  const handleRemoveMetaField = (index: number) => {
    const updatedFields = metaFields.filter((_, i) => i !== index);
    setMetaFields(updatedFields);
    
    // Update formData.meta_data after removing field
    const metaData: Record<string, any> = {};
    updatedFields.forEach(({ key, value }) => {
      if (key && value) {
        metaData[key] = value;
      }
    });
    setFormData(prev => ({ ...prev, meta_data: metaData }));
  };

  const handleMetaFieldChange = (index: number, field: 'key' | 'value', value: string) => {
    const updatedFields = [...metaFields];
    updatedFields[index][field] = value;
    setMetaFields(updatedFields);

    // Update formData.meta_data
    const metaData: Record<string, any> = {};
    updatedFields.forEach(({ key, value }) => {
      if (key && value) {
        metaData[key] = value;
      }
    });
    setFormData(prev => ({ ...prev, meta_data: metaData }));
  };

  const handleAddGroup = () => {
    setFormData(prev => ({
      ...prev,
      groups: [...prev.groups, { group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] }]
    }));
  };

  const handleRemoveGroup = (index: number) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.filter((_, i) => i !== index)
    }));
  };

  const handleGroupChange = (index: number, field: 'group_key' | 'group_name', value: string) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.map((group, i) => {
        if (i === index) {
          if (field === 'group_key') {
            const selectedGroup = availableGroups.find(g => g.group_key === value);

            return {
              ...group,
              group_key: value,
              group_name: selectedGroup ? selectedGroup.group_name : '',
              roles: [{ role_key: '', role_name: '' }] 
            };
          } else {
            return { ...group, [field]: value };
          }
        }
        return group;
      })
    }));
  };

  const getRolesForGroup = (groupKey: string): Role[] => {
    if (!groupKey) {
      return [];
    }
        const selectedGroup = availableGroups.find(g => g.group_key === groupKey);
    return selectedGroup ? selectedGroup.roles : [];
  };

  const handleAddRole = (groupIndex: number) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.map((group, i) =>
        i === groupIndex
          ? { ...group, roles: [...group.roles, { role_key: '', role_name: '' }] }
          : group
      )
    }));
  };

  const handleRemoveRole = (groupIndex: number, roleIndex: number) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.map((group, i) =>
        i === groupIndex
          ? { ...group, roles: group.roles.filter((_, ri) => ri !== roleIndex) }
          : group
      )
    }));
  };

  const handleRoleChange = (groupIndex: number, roleIndex: number, field: 'role_key' | 'role_name', value: string) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.map((group, gi) =>
        gi === groupIndex
          ? {
            ...group,
            roles: group.roles.map((role, ri) => {
              if (ri === roleIndex) {
                if (field === 'role_key') {
                   const rolesForThisGroup = getRolesForGroup(group.group_key);
                  
                  // Auto-populate role_name when role_key is selected  
                  const selectedRole = rolesForThisGroup .find(r => r.role_key === value);
                  return {
                    role_key: value,
                    role_name: selectedRole?.role_name || ''
                  };
                } else {
                  return { ...role, [field]: value };
                }
              }
              return role;
            })
          }
          : group
      )
    }));
  };

  const handleDelete = (userId: string, userName: string) => {
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      await userManagementV2Service.deleteUser(userToDelete.id);
      setShowDeleteModal(false);
      setUserToDelete(null);
      toast.success('Employee deleted successfully')
      await loadUsers(); // Reload the list
      await loadMetrics(); // Refresh metrics
      
    } catch (error: any) {
      toast.error('Error occurred while deleting employee')
      setError(error.message || 'Failed to delete employee');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleEdit = (user: UserV2) => {
    setEditingUser(user);
    // Populate form with user data
    setFormData({
      email: user.email,
      given_name: user.given_name,
      family_name: user.family_name,
       password: '',
      personal_email: user.personal_email || '',
      employee_id: user.employee_id || '',
      mobile_number: user.mobile_number ||'',
      profile_image: user.profile_image || '',
      groups: user.groups || [{ group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] }],
      meta_data: user.meta_data || {},
      linked_social_accounts: user.linked_social_accounts || []
    });

    // Convert metadata to fields
    const metaEntries = Object.entries(user.meta_data || {});
    if (metaEntries.length > 0) {
      setMetaFields(metaEntries.map(([key, value]) => ({ key, value: String(value) })));
    }

    setShowEditModal(true);
  };

  const handleRestore = async (userId: string, userName: string) => {
    try {
      setLoading(true);
      await userManagementV2Service.restoreUser(userId);
      toast.success(`${userName} restored successfully!`);
      await loadUsers();
      await loadMetrics();
    } catch (error: any) {
      console.error('Error restoring user:', error);
      toast.error(error.message || 'Failed to restore user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (userId: string, userName: string) => {
    try {
      setLoading(true);
      await userManagementV2Service.unlockUser(userId);
      toast.success(`${userName} account unlocked successfully!`);
      await loadUsers();
      await loadMetrics();
    } catch (error: any) {
      console.error('Error unlocking user:', error);
      toast.error(error.message || 'Failed to unlock user');
    } finally {
      setLoading(false);
    }
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!formData.given_name) {
      errors.given_name = 'First Name is required';
    }
    if (!formData.family_name) {
      errors.family_name = 'Last Name is required';
    }
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email address is invalid';
    }
      if (!editingUser && !formData.password) {
        errors.password = 'Password is required';
    }
    if (formData.password && formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
    }
    if (!formData.groups[0]?.group_key) {
        errors.department = 'At least one department is required';
    }
    if (!formData.groups[0]?.roles[0]?.role_key) {
        errors.roles = 'At least one role is required for the department';
    }


    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return; // Stop submission if there are errors
    }

    for (let i = 0; i < formData.groups.length; i++) {
      const group = formData.groups[i];
      if (group.roles.length === 0) {
        setError(`Department ${i + 1} must have at least one role assigned`);
        return;
      }

      // Validate that each role has a role_key selected
      for (let j = 0; j < group.roles.length; j++) {
        if (!group.roles[j].role_key) {
          setError(`Please select a role for Department ${i + 1}`);
          return;
        }
      }
    }

    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});

      if (editingUser) {
        // Update existing user - encrypt email
        const requestId = generateRequestId();
        const encryptedEmail = encryptAES(formData.email, requestId);
        const updateData = {
          ...formData,
          request_id: requestId,
          email: encryptedEmail,
        };
        await userManagementV2Service.updateUser(editingUser._id || '', updateData as any);
        toast.success("Employee Updated Succesfully")
        setShowEditModal(false);
      } else {
        // Create new user - encrypt email and password
        const requestId = generateRequestId();
        const encryptedEmail = encryptAES(formData.email, requestId);
        const encryptedPassword = formData.password ? encryptAES(formData.password, requestId) : '';
        const createData = {
          ...formData,
          request_id: requestId,
          email: encryptedEmail,
          password: encryptedPassword,
        };
        await userManagementV2Service.createUser(createData);
        toast.success("Employee Added Succesfully")
        setShowAddModal(false);
      }

      resetForm();
      setEditingUser(null);
      loadUsers(); // Refresh the users list
      loadMetrics(); // Refresh metrics
    } catch (error: any) {
      const errorMessage = error.message || `Failed to ${editingUser ? 'update' : 'create'} user`;
      setError(errorMessage);
      toast.error(errorMessage)

      // Set field-specific errors for duplicate entries
      if (errorMessage.toLowerCase().includes('mobile number') && errorMessage.toLowerCase().includes('already registered')) {
        setFieldErrors(prev => ({ ...prev, mobile_number: 'This mobile number is already registered' }));
      }
      if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already registered')) {
        setFieldErrors(prev => ({ ...prev, email: 'This email is already registered' }));
      }
    } finally {
      setLoading(false);
    }
  };

  const generateSampleCSV = () => {
    const csvContent = `email,given_name,family_name,password,mobile_number,employee_id,personal_email,groups,status,bu_head_email,reporting_manager_email,hrbp_email
john@company.com,John,Doe,SecurePass123!,+1234567890,EMP001,john.doe@personal.com,"admin(user_admin,content_admin)|sales(sales_rep)",ACTIVE,buhead@company.com,manager@company.com,hr@company.com
jane@company.com,Jane,Smith,SecurePass456!,+1234567891,EMP002,jane.smith@personal.com,"hr(hr_manager)",ACTIVE,buhead@company.com,manager@company.com,hr@company.com
bob@company.com,Bob,Johnson,SecurePass789!,+1234567892,EMP003,bob.johnson@personal.com,"engineering(senior_developer,tech_lead)",INACTIVE,buhead@company.com,manager@company.com,hr@company.com
alice@company.com,Alice,Williams,SecurePass321!,+1234567893,EMP004,alice.w@personal.com,"sales(sales_rep)",SUSPENDED,buhead@company.com,manager@company.com,hr@company.com
charlie@company.com,Charlie,Brown,SecurePass654!,+1234567894,EMP005,charlie.b@personal.com,"support(support_agent)",LOCKED,buhead@company.com,manager@company.com,hr@company.com`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'sample_users_import.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setBulkImportFile(file);

    // Clear previous import status and errors when new file is selected
    setBulkImportStatus(null);
    setImportRecords([]);

    try {
      setUploadingFile(true);
      setError(null);

      // Upload file immediately when selected
      const uploadResponse = await userManagementV2Service.uploadFile(file);
      setUploadedFileUrl(uploadResponse.data.url);
      setUploadedFileName(uploadResponse.data.name);

    } catch (error: any) {
      setError(error.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleBulkImport = async () => {
    if (!uploadedFileUrl || !uploadedFileName) {
      setError('Please upload a CSV file first');
      return;
    }

    try {
      setProcessingImport(true);
      setError(null);

      // Initiate bulk import using the already uploaded file URL
      const importResponse = await userManagementV2Service.initiateBulkImport({
        csv_url: uploadedFileUrl,
        original_filename: uploadedFileName!,  // We know this exists since we check for uploadedFileUrl
        duplicate_handling: duplicateHandling,
        auto_verify_email: false,
        auto_verify_mobile: false,
        default_status: 'ACTIVE',
      });

      setBulkImportStatus({
        import_id: importResponse.data.import_id,
        status: importResponse.data.status,
        metrics: {
          total_records: 0,
          processed_records: 0,
          success_count: 0,
          failed_count: 0,
          skipped_count: 0,
          progress_percentage: 0,
        },
        total_records: 0,
        processed_records: 0,
        successful_records: 0,
        failed_records: 0,
        skipped_records: 0,
        start_time: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

      // Start polling for status
      pollImportStatus(importResponse.data.import_id);

    } catch (error: any) {
      setError(error.message || 'Failed to start bulk import');
      setProcessingImport(false);
    }
  };

  const pollImportStatus = async (importId: string) => {
    try {
      const statusResponse = await userManagementV2Service.getBulkImportStatus(importId);
      setBulkImportStatus(statusResponse.data);

      if (statusResponse.data.status === 'PROCESSING' || statusResponse.data.status === 'PENDING' || statusResponse.data.status === 'INITIATED') {
        // Continue polling every 2 seconds
        setTimeout(() => pollImportStatus(importId), 2000);
      } else {
        // Import completed or failed
        setProcessingImport(false);
        if (statusResponse.data.status === 'COMPLETED') {
          loadUsers(); // Refresh user list
          loadMetrics(); // Refresh metrics
        }
      }
    } catch (error) {
      console.error('Error polling import status:', error);
      setProcessingImport(false);
    }
  };

  const resetBulkImport = () => {
    setBulkImportFile(null);
    setUploadedFileUrl(null);
    setUploadedFileName(null);
    setBulkImportStatus(null);
    setUploadingFile(false);
    setProcessingImport(false);
    setDuplicateHandling('SKIP');
  };

  const loadImportHistory = async (page: number = 1, limit?: number) => {
    try {
      setLoadingHistory(true);
      const currentLimit = limit || historyLimit;
      const response = await userManagementV2Service.getBulkImportHistory({ page, limit: currentLimit });
      // Access the imports array from the correct path
      const historyData = response.data?.imports || [];
      setImportHistory(historyData);
      setHistoryTotal(response.data?.total || 0);
      setHistoryTotalPages(Math.ceil((response.data?.total || 0) / currentLimit));
      setHistoryPage(page);
    } catch (error: any) {
      console.error('Error loading import history:', error);
      setImportHistory([]); // Set empty array on error
      setHistoryTotal(0);
      setHistoryTotalPages(0);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleShowHistory = () => {
    setShowBulkDropdown(false);
    loadImportHistory();
    setShowImportHistoryModal(true);
  };

  const loadImportRecords = async (importId: string, page: number = 1) => {
    try {
      setLoadingRecords(true);
      const params: any = {
        page,
        limit: recordsLimit,
      };

      if (recordsFilter !== 'ALL') {
        params.status = recordsFilter;
      }

      if (recordsSearch) {
        params.search = recordsSearch;
      }

      const response = await userManagementV2Service.getBulkImportRecords(importId, params);

      // Access the records from the data object
      const recordsData = response.data?.records || [];
      setImportRecords(recordsData);
      setRecordsTotal(response.data?.total || 0);
      setRecordsTotalPages(Math.ceil((response.data?.total || 0) / recordsLimit));
      setRecordsPage(page);

    } catch (error: any) {
      console.error('Error loading import records:', error);
      setImportRecords([]);
      setError(error.message || 'Failed to load import records');
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleViewRecordDetails = (item: BulkImportHistory) => {
    setSelectedHistoryItem(item);
    setRecordsPage(1);
    setRecordsFilter('ALL');
    setRecordsSearch('');
    setShowRecordDetailsModal(true);
    loadImportRecords(item.import_id);
  };

  const handleResetPassword = (user: UserV2) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setShowResetPasswordModal(true);
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const performPasswordReset = async () => {
    if (!resetPasswordUser) return;

    // Validate passwords
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      setResettingPassword(true);
      setPasswordError(null);

      const userId = resetPasswordUser._id || resetPasswordUser.id;
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Encrypt password before sending
      const requestId = generateRequestId();
      const encryptedPassword = encryptAES(newPassword, requestId);

      const response = await userManagementV2Service.resetUserPassword(userId, encryptedPassword, requestId);

      // Success
      setShowResetPasswordModal(false);
      setResetPasswordUser(null);
      setNewPassword('');
      setConfirmPassword('');

      // Show success message
      const successMessage = response.message || `Password reset successfully for ${resetPasswordUser.email}`;
      // You can replace this with a toast notification if you have a toast library
      // For now, we'll use a temporary success state
      setError(null); // Clear any existing errors

      // Optional: Show a temporary success notification
      const tempSuccessDiv = document.createElement('div');
      tempSuccessDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
      tempSuccessDiv.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
        </svg>
        ${successMessage}
      `;
      document.body.appendChild(tempSuccessDiv);
      setTimeout(() => {
        tempSuccessDiv.remove();
      }, 3000);

    } catch (error: any) {
      console.error('Error resetting password:', error);
      setPasswordError(error.message || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const csvFields = [
    { field: 'email', type: 'string', required: true, description: 'Employee email address (must be unique)' },
    { field: 'given_name', type: 'string', required: true, description: 'First name (or use first_name)' },
    { field: 'family_name', type: 'string', required: true, description: 'Last name (or use last_name)' },
    { field: 'password', type: 'string', required: true, description: 'Password (minimum 8 characters)' },
    { field: 'mobile_number', type: 'string', required: false, description: 'Mobile phone number (or use mobile/phone)' },
    { field: 'employee_id', type: 'string', required: false, description: 'Employee ID (or use emp_id)' },
    { field: 'personal_email', type: 'string', required: false, description: 'Personal email address' },
    { field: 'groups', type: 'string', required: true, description: 'Groups and roles: "group1(role1,role2)|group2(role3)"' },
    { field: 'status', type: 'enum', required: false, description: 'User status: ACTIVE, INACTIVE, SUSPENDED, LOCKED, ARCHIVED, DELETED (defaults to ACTIVE)' },
    { field: 'bu_head_email', type: 'string', required: false, description: 'Business Unit Head email address' },
    { field: 'reporting_manager_email', type: 'string', required: false, description: 'Reporting Manager email address' },
    { field: 'hrbp_email', type: 'string', required: false, description: 'HR Business Partner email address' },
  ];

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside for export dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle export functionality using export service
  const exportUsers = async (format: 'csv' | 'excel') => {
    try {
      setExportingUsers(true);
      setShowExportDropdown(false);

      // Use export service to call the export API with filters only (no pagination)
      const result = await exportService.exportUsersList(format, {
        search: debouncedSearchQuery || undefined,
        status: filterStatus,
        group_key: filterDepartment
      });

      if (result.success) {
        toast.success(`Successfully exported ${result.count} employees to ${format.toUpperCase()}`);
      } else {
        toast.error(result.message || 'No employees found to export');
      }
    } catch (error: any) {
      console.error('Export error details:', error);
      let errorMessage = 'Failed to export employees';

      if (error?.response?.status === 404) {
        errorMessage = 'Export feature is not available at this time';
      } else if (error?.response?.status === 500) {
        errorMessage = 'Server error occurred during export. Please try again later.';
      } else if (error?.message) {
        errorMessage = `Export failed: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setExportingUsers(false);
    }
  };
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Employee Management</h1>
            <p className="text-blue-100">
              Manage employees, roles, and integrate with your identity provider
            </p>
          </div>
          <div className="flex gap-3">
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => canWrite && setShowExportDropdown(!showExportDropdown)}
                disabled={!canWrite || exportingUsers}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canWrite ? 'You do not have permission' : 'Export employees'}
              >
                {exportingUsers ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span className="hidden sm:inline">Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span className="hidden sm:inline">Export</span>
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>

              {showExportDropdown && (
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => exportUsers('csv')}
                    className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-t-lg"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Export as CSV</span>
                  </button>
                  <button
                    onClick={() => exportUsers('excel')}
                    className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-b-lg"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Export as Excel</span>
                  </button>
                </div>
              )}
            </div>

            <div className="relative" ref={bulkDropdownRef}>
              <div className="flex">
                <button
                  onClick={() => canWrite && setShowBulkImportModal(true)}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission' : undefined}
                  className={`flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-l-lg transition-colors ${
                    canWrite ? 'hover:bg-white/30' : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Upload className="w-5 h-5" />
                  <span className="hidden sm:inline">Bulk Upload</span>
                </button>
                <button
                  onClick={() => setShowBulkDropdown(!showBulkDropdown)}
                  className="px-2 py-2 bg-white/20 backdrop-blur-sm rounded-r-lg hover:bg-white/30 transition-colors border-l border-white/30"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              {showBulkDropdown && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg z-10">
                  <button
                    onClick={handleShowHistory}
                    className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
                  >
                    <History className="w-4 h-4" />
                    <span className="text-sm">Import History</span>
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => canWrite && setShowAddModal(true)}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg transition-colors ${
                canWrite ? 'hover:bg-blue-50' : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <UserPlus className="w-5 h-5" />
              <span className="hidden sm:inline">Add Employee</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Total Employees</p>
              <p className="text-2xl font-bold">
                {loadingMetrics ? '...' : (metrics?.total_users || totalUsers || '0')}
              </p>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500/30 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-300" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Active Employees</p>
              <p className="text-2xl font-bold">
                {loadingMetrics ? '...' : (metrics?.active_users || users.filter(u => u.status === 'ACTIVE').length || '0')}
              </p>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-500/30 rounded-lg">
              <Clock className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Pending Invites</p>
              <p className="text-2xl font-bold">
                {loadingMetrics ? '...' : (metrics?.pending_invites || '0')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <Controller
              name="search"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Search employees by name, email, or employee ID..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoComplete="off"
                />
              )}
            />
          </div>
          <div className="min-w-[180px]">
            <Select<FilterFormData>
              name="department"
              control={control}
              options={[
                { value: 'all', label: 'All Departments' },
                ...availableGroups.map(group => ({
                  value: group.group_key || '',
                  label: group.group_name
                }))
              ]}
              placeholder="All Departments"
            />
          </div>
          <div className="min-w-[150px]">
            <Select<FilterFormData>
              name="status"
              control={control}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'ACTIVE', label: '🟢 Active' },
                { value: 'INACTIVE', label: '🟡 Inactive' },
                { value: 'SUSPENDED', label: '🔴 Suspended' },
                { value: 'LOCKED', label: '🔒 Locked' },
                { value: 'ARCHIVED', label: '📦 Archived' },
                { value: 'DELETED', label: '🗑️ Deleted' }
              ]}
              placeholder="All Status"
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <TableSkeleton rows={10} columns={8} showHeader={true} />
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No employees found. Click "Add Employee" to create your first employee.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Employee ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Courses</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Certifications</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Last Active</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => {
                  // Use real course data from API
                  const coursesAssigned = user.courses?.assigned || 0;
                  const coursesCompleted = user.courses?.completed || 0;
                  const progressPercentage = coursesAssigned > 0 ? (coursesCompleted / coursesAssigned) * 100 : 0;

                  // Use real certification data from API
                  const certificationsAssigned = (user as any).certifications?.assigned || 0;
                  const certificationsCompleted = (user as any).certifications?.completed || 0;
                  const certProgressPercentage = certificationsAssigned > 0 ? (certificationsCompleted / certificationsAssigned) * 100 : 0;

                  // Format last active time
                  const formatLastActive = () => {
                    if (!user?.updated_at) return 'Never';
                    if (!user?.last_login) return 'Never';

                    const lastActiveDate = new Date(user.last_login);
                    const now = new Date();
                    const diffTime = Math.abs(
                      now.getTime() - lastActiveDate.getTime(),
                    );
                    const diffDays = Math.floor(
                      diffTime / (1000 * 60 * 60 * 24),
                    );

                    if (diffDays === 0) return 'Today';
                    if (diffDays === 1) return 'Yesterday';
                    if (diffDays < 30) return `${diffDays} days ago`;

                    return lastActiveDate.toLocaleDateString();
                  };


                  const userId = user._id || user.id || '';

                  return (
                    <tr key={userId} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {user.profile_image ? (
                            <img
                              src={user.profile_image}
                              alt={`${user.given_name} ${user.family_name || ''}`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.given_name[0]}{user.family_name[0] || ''}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{user.full_name || `${user.given_name} ${user.family_name || ''}`}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-900">{user.employee_id || '-'}</span>
                      </td>
                      <td className="px-4 py-4">
                        <UserStatusBadge status={user.status as any} size="sm" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">
                          {(user.groups?.length ?? 0) > 0 ?
                            user.groups!.map(g => g.group_name).join(', ') :
                            'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">{coursesCompleted}</span>
                            <span className="text-gray-500">/{coursesAssigned}</span>
                          </div>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${progressPercentage}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">{certificationsCompleted}</span>
                            <span className="text-gray-500">/{certificationsAssigned}</span>
                          </div>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-amber-500 h-2 rounded-full"
                              style={{ width: `${certProgressPercentage}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-500">{formatLastActive()}</span>
                      </td>
                      <td className="px-4 py-4">
                        {(user.status as string) === 'DELETED' ? (
                          // Deleted user - Show only Restore action
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => canWrite && handleRestore(user._id || '', `${user.given_name} ${user.family_name}`)}
                              disabled={!canWrite}
                              className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!canWrite ? 'You do not have permission' : 'Restore user (undo delete)'}
                            >
                              <RefreshCw className="w-4 h-4" />
                              Restore
                            </button>
                          </div>
                        ) : (user.status as string) === 'LOCKED' ? (
                          // Locked user - Show Unlock button prominently + other actions
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => canWrite && handleUnlock(user._id || '', `${user.given_name} ${user.family_name}`)}
                              disabled={!canWrite}
                              className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!canWrite ? 'You do not have permission' : 'Unlock account'}
                            >
                              <Lock className="w-4 h-4" />
                              Unlock
                            </button>
                            <button
                              onClick={() => canWrite && handleEdit(user)}
                              disabled={!canWrite}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!canWrite ? 'You do not have permission' : 'Edit Employee'}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          // Regular user - Show all actions
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(
                                `/admin/users/${user._id}/courses`,
                                { state: { from: '/admin/users' } }
                              )}
                              className="p-1 text-gray-400 hover:text-primary-600"
                              title="View Courses"
                            >
                              <Book className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => navigate(
                                `/admin/users/${user._id}/certifications`,
                                { state: { from: '/admin/users' } }
                              )}
                              className="p-1 text-gray-400 hover:text-amber-600"
                              title="View Certifications"
                            >
                              <Award className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => canWrite && handleResetPassword(user)}
                              disabled={!canWrite}
                              className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!canWrite ? 'You do not have permission' : 'Reset Password'}
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => canWrite && handleEdit(user)}
                              disabled={!canWrite}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!canWrite ? 'You do not have permission' : 'Edit Employee'}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => canWrite && handleDelete(user._id || '', `${user.given_name} ${user.family_name}`)}
                              disabled={!canWrite}
                              className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!canWrite ? 'You do not have permission' : 'Delete Employee'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalUsers > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalUsers}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setCurrentPage(1);
            }}
            showItemsPerPage={true}
          />
        )}
      </div>

      {/* OLD Add User Modal - Replaced with UserFormModal */}
      {false && showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Add New Employee</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.given_name}
                       onChange={(e) => {
                        setFormData(prev => ({ ...prev, given_name: e.target.value }));
                          if (fieldErrors.given_name) setFieldErrors(prev => ({ ...prev, given_name: '' }));
                           }}
                          className={`w-full px-3 py-2 border ${fieldErrors.given_name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.given_name ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                          />
                       {fieldErrors.given_name && <p className="mt-1 text-sm text-red-600">{fieldErrors.given_name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.family_name}
                       onChange={(e) => {
                       setFormData(prev => ({ ...prev, family_name: e.target.value }));
                       if (fieldErrors.family_name) setFieldErrors(prev => ({ ...prev, family_name: '' }));
                       }}
                      className={`w-full px-3 py-2 border ${fieldErrors.family_name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.family_name ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                        />
                    {fieldErrors.family_name && <p className="mt-1 text-sm text-red-600">{fieldErrors.family_name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                     onChange={(e) => {
                      setFormData(prev => ({ ...prev, email: e.target.value }));
                      if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
                      }}
                      className={`w-full px-3 py-2 border ${fieldErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.email ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                      />
                    {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
                  </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showAddPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, password: e.target.value }));
                          if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: '' }));
                        }}
                        className={`w-full px-3 py-2 pr-10 border ${fieldErrors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.password ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                        placeholder="Enter password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowAddPassword(!showAddPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {showAddPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                      </button>
                    </div>
                    {fieldErrors.password && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile_number}
                       onChange={(e) => {
                          setFormData(prev => ({ ...prev, mobile_number: e.target.value }));
                          if (fieldErrors.mobile_number) setFieldErrors(prev => ({ ...prev, mobile_number: '' }));
                           }}
                          className={`w-full px-3 py-2 border ${fieldErrors.mobile_number ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.mobile_number ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                          />
                     {fieldErrors.mobile_number && <p className="mt-1 text-sm text-red-600">{fieldErrors.mobile_number}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Personal Email
                    </label>
                    <input
                      type="email"
                      value={formData.personal_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, personal_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={formData.employee_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Profile Photo
                    </label>
                    <FileUploadField
                      description="Upload a profile photo for the user"
                      currentFileUrl={formData.profile_image || undefined}
                      onFileChange={(url) => setFormData(prev => ({ ...prev, profile_image: url || '' }))}
                      onRemove={() => setFormData(prev => ({ ...prev, profile_image: '' }))}
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={2}
                      metadata={{ category: 'user', type: 'profile-image' }}
                    />
                  </div>
                </div>
              </div>

              {/* Department and Roles */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Department & Roles *</h3>
                  <button
                    type="button"
                    onClick={handleAddGroup}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Department
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  At least one department assignment is required. Select departments and assign roles.
                </p>
                <div className="space-y-4">
                  {formData.groups.map((group, groupIndex) => (
                    <div key={groupIndex} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Department {groupIndex + 1}</h4>
                        {formData.groups.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveGroup(groupIndex)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Department *
                          </label>
                          <select
                            value={group.group_key}
                            onChange={(e) => {
                              handleGroupChange(groupIndex, 'group_key', e.target.value);
                              if(fieldErrors.department) setFieldErrors(prev => ({ ...prev, department: ''}));
                              }}
                            className={`w-full px-3 py-2 border ${fieldErrors.department ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.department ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                            disabled={loadingOptions}
                             >
                            <option value="">
                              {loadingOptions ? 'Loading departments...' : 'Choose a department...'}
                            </option>
                            {availableGroups
                              .filter(g =>
                                g.group_key === group.group_key ||
                                !formData.groups.some(selectedGroup => selectedGroup.group_key === g.group_key)
                              )
                              .map((g) => (
                                <option
                                  key={g._id}
                                  value={g.group_key || ''} // Keep the fallback for TypeScript
                                >
                                  {g.group_name}
                                </option>
                            ))}
                          </select>
                          {groupIndex === 0 && fieldErrors.department && (
                            <p className="mt-1 text-sm text-red-600">{fieldErrors.department}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Department Name
                          </label>
                          <input
                            type="text"
                            placeholder="Auto-populated from selection"
                            value={group.group_name || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                            readOnly
                            disabled
                          />
                        </div>
                      </div>

                      {/* Roles */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">Roles *</label>
                          <button
                            type="button"
                            onClick={() => handleAddRole(groupIndex)}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            + Add Role
                          </button>
                        </div>
                        <div className="space-y-2">
                          {group.roles.map((role, roleIndex) => (
                            <div key={roleIndex} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div>
                                <select
                                  value={role.role_key}
                                  onChange={(e) => {handleRoleChange(groupIndex, roleIndex, 'role_key', e.target.value);
                                           if(fieldErrors.roles) setFieldErrors(prev => ({...prev, roles: ''}));
                                          }}
                                  className={`w-full px-3 py-2 border ${fieldErrors.roles ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.roles ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                                  disabled={loadingOptions || !group.group_key}
                                  >
                                  <option value="">
                                    {loadingOptions ? 'Loading roles...' : 'Choose a role...'}
                                  </option>
                                  {getRolesForGroup(group.group_key)
                                    .filter(r =>
                                      r.role_key === role.role_key ||
                                      !group.roles.some((selectedRole, idx) => idx !== roleIndex && selectedRole.role_key === r.role_key)
                                    )
                                    .map((r) => (
                                      <option key={r._id} value={r.role_key}>
                                        {r.role_name} ({r.role_key})
                                      </option>
                                    ))}
                                </select>
                                {groupIndex === 0 && roleIndex === 0 && fieldErrors.roles && (
                                  <p className="mt-1 text-sm text-red-600">{fieldErrors.roles}</p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Auto-populated"
                                  value={role.role_name || ''}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                  readOnly
                                  disabled
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveRole(groupIndex, roleIndex)}
                                  className="p-2 text-red-600 hover:text-red-700"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                          {group.roles.length === 0 && (
                            <p className="text-sm text-red-500 italic">
                              At least one role is required. Click "+ Add Role" to assign roles to this department.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Metadata */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Custom Metadata</h3>
                  <button
                    type="button"
                    onClick={handleAddMetaField}
                    className="flex items-center gap-1 px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Field
                  </button>
                </div>
                <div className="space-y-2">
                  {metaFields.map((field, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Key (e.g. department)"
                        value={field.key}
                        onChange={(e) => handleMetaFieldChange(index, 'key', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Value (e.g. Engineering)"
                        value={field.value}
                        onChange={(e) => handleMetaFieldChange(index, 'value', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveMetaField(index)}
                        className="p-2 text-red-600 hover:text-red-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Add custom fields like department, location, hire_date, etc.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  // disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Creating...' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* OLD Edit User Modal - Replaced with UserFormModal */}
      {false && showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Edit Employee</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.given_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, given_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.family_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, family_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, email: e.target.value }));
                        if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
                      }}
                      className={`w-full px-3 py-2 border ${fieldErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.email ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                      required
                    />
                    {fieldErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile_number}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, mobile_number: e.target.value }));
                        if (fieldErrors.mobile_number) setFieldErrors(prev => ({ ...prev, mobile_number: '' }));
                      }}
                      className={`w-full px-3 py-2 border ${fieldErrors.mobile_number ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${fieldErrors.mobile_number ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
                      required
                    />
                    {fieldErrors.mobile_number && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.mobile_number}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Personal Email
                    </label>
                    <input
                      type="email"
                      value={formData.personal_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, personal_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={formData.employee_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Profile Photo
                    </label>
                    <FileUploadField
                      description="Upload a profile photo for the user"
                      currentFileUrl={formData.profile_image || undefined}
                      onFileChange={(url) => setFormData(prev => ({ ...prev, profile_image: url || '' }))}
                      onRemove={() => setFormData(prev => ({ ...prev, profile_image: '' }))}
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={2}
                      metadata={{ category: 'user', type: 'profile-image' }}
                    />
                  </div>
                </div>
              </div>

              {/* Department and Roles */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Department & Roles *</h3>
                  <button
                    type="button"
                    onClick={handleAddGroup}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Department
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  At least one department assignment is required. Select departments and assign roles.
                </p>
                <div className="space-y-4">
                  {formData.groups.map((group, groupIndex) => (
                    <div key={groupIndex} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Department {groupIndex + 1}</h4>
                        {formData.groups.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveGroup(groupIndex)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Department *
                          </label>
                          <select
                            value={group.group_key}
                            onChange={(e) => handleGroupChange(groupIndex, 'group_key', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                            disabled={loadingOptions}
                          >
                            <option value="">
                              {loadingOptions ? 'Loading departments...' : 'Choose a department...'}
                            </option>
                            {availableGroups
                              .filter(g =>
                                g.group_key === group.group_key ||
                                !formData.groups.some(selectedGroup => selectedGroup.group_key === g.group_key)//If you select one department and then select a second department, the first selected department should not appear in the dropdown.
                              )
                              .map((g) => (
                                <option
                                  key={g._id}
                                  value={g.group_key || ''}
                                >
                                  {g.group_name}
                                </option>
                              ))}
                          </select>
                          {groupIndex === 0 && fieldErrors.department && (
                            <p className="mt-1 text-sm text-red-600">{fieldErrors.department}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Department Name
                          </label>
                          <input
                            type="text"
                            placeholder="Auto-populated from selection"
                            value={group.group_name || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                            readOnly
                            disabled
                          />
                        </div>
                      </div>

                      {/* Roles */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">Roles *</label>
                          <button
                            type="button"
                            onClick={() => handleAddRole(groupIndex)}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            + Add Role
                          </button>
                        </div>
                        <div className="space-y-2">
                          {group.roles.map((role, roleIndex) => (
                            <div key={roleIndex} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div>
                                <select
                                  value={role.role_key}
                                  onChange={(e) => handleRoleChange(groupIndex, roleIndex, 'role_key', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={loadingOptions}
                                  required
                                >
                                  <option value="">
                                    {loadingOptions ? 'Loading roles...' : 'Choose a role...'}
                                  </option>
                                  {getRolesForGroup(group.group_key)
                                    .filter(r =>
                                      r.role_key === role.role_key ||
                                      !group.roles.some((selectedRole, idx) => idx !== roleIndex && selectedRole.role_key === r.role_key)
                                    )
                                    .map((r) => (
                                      <option key={r._id} value={r.role_key}>
                                        {r.role_name} ({r.role_key})
                                      </option>
                                    ))}
                                </select>
                                {groupIndex === 0 && roleIndex === 0 && fieldErrors.roles && (
                                  <p className="mt-1 text-sm text-red-600">{fieldErrors.roles}</p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Auto-populated"
                                  value={role.role_name || ''}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                  readOnly
                                  disabled
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveRole(groupIndex, roleIndex)}
                                  className="p-2 text-red-600 hover:text-red-700"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                          {group.roles.length === 0 && (
                            <p className="text-sm text-red-500 italic">
                              At least one role is required. Click "+ Add Role" to assign roles to this department.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Metadata */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Custom Metadata</h3>
                  <button
                    type="button"
                    onClick={handleAddMetaField}
                    className="flex items-center gap-1 px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Field
                  </button>
                </div>
                <div className="space-y-2">
                  {metaFields.map((field, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Key (e.g. department)"
                        value={field.key}
                        onChange={(e) => handleMetaFieldChange(index, 'key', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Value (e.g. Engineering)"
                        value={field.value}
                        onChange={(e) => handleMetaFieldChange(index, 'value', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveMetaField(index)}
                        className="p-2 text-red-600 hover:text-red-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Add custom fields like department, location, hire_date, etc.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Updating...' : 'Update Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Bulk Import Employees</h2>
              <button
                onClick={() => {
                  setShowBulkImportModal(false);
                  resetBulkImport();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {!bulkImportStatus ? (
              <>
                {/* CSV Format Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">CSV Format Requirements</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {csvFields.map((field, index) => (
                          <tr key={index} className={field.required ? 'bg-blue-50' : ''}>
                            <td className="px-4 py-2 font-mono text-sm font-medium text-gray-900">
                              {field.field}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">{field.type}</td>
                            <td className="px-4 py-2 text-sm">
                              {field.required ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  Required
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  Optional
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">{field.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium text-yellow-800 mb-2">Groups Format:</h4>
                    <p className="text-yellow-700 text-sm">
                      Use format: <code className="bg-yellow-100 px-1 py-0.5 rounded">"group1(role1,role2)|group2(role3)"</code>
                    </p>
                    <p className="text-yellow-700 text-sm mt-1">
                      Example: <code className="bg-yellow-100 px-1 py-0.5 rounded">"admin(user_admin,content_admin)|sales(sales_rep)"</code>
                    </p>
                    <div className="mt-3 p-3 border-l-4 border-yellow-400 bg-yellow-50 rounded-md">
                      <div className="flex items-start space-x-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-yellow-600 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.054 0 1.932-.816 1.994-1.851L21 5.851A2 2 0 0018.856 4H5.144A2 2 0 003 5.851l.088 11.298A2 2 0 005.144 19z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-yellow-800">Instruction</p>
                          <p className="text-sm text-yellow-700 mt-1 leading-relaxed">
                            Please use the <code className="bg-yellow-100 px-1 py-0.5 rounded text-yellow-800 font-medium">group_key</code> and
                            <code className="bg-yellow-100 px-1 py-0.5 rounded text-yellow-800 font-medium ml-1">role_key</code> for Group format.
                            <br />
                            Make sure the group is properly mapped to a role.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">Status Field (Optional):</h4>
                    <p className="text-blue-700 text-sm mb-2">
                      The <code className="bg-blue-100 px-1 py-0.5 rounded">status</code> field is optional. If not provided, users will be created with ACTIVE status.
                    </p>
                    <div className="space-y-2">
                      <p className="text-blue-700 text-sm font-semibold">Valid Status Values:</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-medium">ACTIVE</span>
                          <span className="text-blue-700">Full access (default)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded font-medium">INACTIVE</span>
                          <span className="text-blue-700">Temporarily disabled</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-medium">SUSPENDED</span>
                          <span className="text-blue-700">Policy violation</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded font-medium">LOCKED</span>
                          <span className="text-blue-700">Security lockout</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded font-medium">ARCHIVED</span>
                          <span className="text-blue-700">Offboarded employee</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded font-medium">DELETED</span>
                          <span className="text-blue-700">Marked for deletion</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-blue-700 text-sm mt-3">
                      Example: <code className="bg-blue-100 px-1 py-0.5 rounded">ACTIVE</code> or <code className="bg-blue-100 px-1 py-0.5 rounded">INACTIVE</code> or <code className="bg-blue-100 px-1 py-0.5 rounded">LOCKED</code>
                    </p>
                    <div className="mt-3 p-3 bg-blue-100 rounded-md">
                      <p className="text-xs text-blue-800">
                        <strong>Note:</strong> Status values are case-insensitive. Invalid values will fall back to the default status setting.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sample CSV Download */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Sample CSV</h3>
                  <button
                    onClick={generateSampleCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Download Sample CSV
                  </button>
                  <p className="text-sm text-gray-600 mt-2">
                    Download a sample CSV file with the correct format and example data.
                  </p>
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Upload CSV File</h3>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="w-12 h-12 text-gray-400" />
                      <p className="text-lg font-medium text-gray-900">
                        Click to select CSV file
                      </p>
                      <p className="text-sm text-gray-600">
                        Only .csv files are accepted
                      </p>
                    </label>

                    {uploadingFile && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-blue-800 font-medium">Uploading {bulkImportFile?.name}...</p>
                        <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                          <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                        </div>
                      </div>
                    )}

                    {uploadedFileUrl && !uploadingFile && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 font-medium">✓ {uploadedFileName}</p>
                        <p className="text-green-600 text-sm">
                          File uploaded successfully. Size: {(bulkImportFile?.size ? bulkImportFile.size / 1024 : 0).toFixed(2)} KB
                        </p>
                      </div>
                    )}

                    {bulkImportFile && !uploadedFileUrl && !uploadingFile && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 font-medium">Upload failed for {bulkImportFile.name}</p>
                        <p className="text-red-600 text-sm">
                          Please try selecting the file again.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Import Options */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Import Options</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duplicate Handling
                      </label>
                      <p className="text-sm text-gray-600 mb-2">
                        How should duplicate email addresses be handled?
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="duplicates"
                            value="SKIP"
                            checked={duplicateHandling === 'SKIP'}
                            onChange={(e) => setDuplicateHandling(e.target.value as 'SKIP' | 'UPDATE' | 'ERROR')}
                            className="mr-2"
                          />
                          <span className="text-sm">Skip - Don't import duplicate users</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="duplicates"
                            value="UPDATE"
                            checked={duplicateHandling === 'UPDATE'}
                            onChange={(e) => setDuplicateHandling(e.target.value as 'SKIP' | 'UPDATE' | 'ERROR')}
                            className="mr-2"
                          />
                          <span className="text-sm">Update - Update existing user information</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="duplicates"
                            value="ERROR"
                            checked={duplicateHandling === 'ERROR'}
                            onChange={(e) => setDuplicateHandling(e.target.value as 'SKIP' | 'UPDATE' | 'ERROR')}
                            className="mr-2"
                          />
                          <span className="text-sm">Error - Stop import on duplicate found</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBulkImportModal(false);
                      resetBulkImport();
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkImport}
                    disabled={!uploadedFileUrl || uploadingFile || processingImport}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-4 h-4" />
                    {processingImport ? 'Starting Import...' : 'Start Import'}
                  </button>
                </div>
              </>
            ) : (
              /* Import Progress */
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Import Progress</h3>

                  <div className="mb-4">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bulkImportStatus.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : bulkImportStatus.status === 'FAILED' ? 'bg-red-100 text-red-700' : bulkImportStatus.status === 'PROCESSING' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {bulkImportStatus.status}
                    </div>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                  <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        bulkImportStatus?.status === 'COMPLETED'
                          ? 'bg-green-600'
                          : bulkImportStatus?.status === 'FAILED'
                          ? 'bg-red-600'
                          : bulkImportStatus?.status === 'PROCESSING'
                          ? 'bg-blue-600'
                          : 'bg-yellow-600'
                      }`}
                      style={{
                        width: `${
                          bulkImportStatus?.status === 'PENDING' || bulkImportStatus?.status === 'INITIATED'
                            ? 0
                            : bulkImportStatus?.metrics?.progress_percentage ?? 0
                        }%`
                      }}
                    />
                  </div>

                  <p className="text-sm text-gray-600 mb-6">
                    {bulkImportStatus?.status === 'PENDING' || bulkImportStatus?.status === 'INITIATED'
                      ? 0
                      : bulkImportStatus?.metrics?.progress_percentage ?? 0}% complete
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">{bulkImportStatus?.metrics?.total_records ?? 0}</p>
                    <p className="text-sm text-gray-600">Total Records</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{bulkImportStatus?.metrics?.success_count ?? 0}</p>
                    <p className="text-sm text-green-600">Successful</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-700">{bulkImportStatus?.metrics?.skipped_count ?? 0}</p>
                    <p className="text-sm text-yellow-600">Skipped</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-700">{bulkImportStatus?.metrics?.failed_count ?? 0}</p>
                    <p className="text-sm text-red-600">Failed</p>
                  </div>
                </div>

                {bulkImportStatus.error_message && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-medium text-red-800 mb-2">Error:</h4>
                    <p className="text-red-700 text-sm">{bulkImportStatus.error_message}</p>
                  </div>
                )}

                {bulkImportStatus.validation_errors && bulkImportStatus.validation_errors.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-medium text-red-800 mb-2">Validation Errors:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {bulkImportStatus.validation_errors.map((error, index) => (
                        <li key={index} className="text-red-700 text-sm">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex gap-3 justify-end pt-4 border-t">
                  {bulkImportStatus.status === 'COMPLETED' || bulkImportStatus.status === 'FAILED' ? (
                    <>
                      <button
                        onClick={() => {
                          setShowBulkImportModal(false);
                          resetBulkImport();
                        }}
                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => resetBulkImport()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Import More Users
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600 py-2">
                      Import is in progress... Please wait.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import History Modal */}
      {showImportHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
          <div className="bg-white rounded-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Bulk Import History</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Showing all recent bulk import jobs.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowImportHistoryModal(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {loadingHistory ? (
              <TableSkeleton rows={5} columns={8} showHeader={true} />
            ) : !Array.isArray(importHistory) || importHistory.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No import history found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Records</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(Array.isArray(importHistory) ? importHistory : []).map((item) => {
                      const getFileProcessedStatus = (item: BulkImportHistory) => {
                        const total = item.metrics?.total_records || 0;
                        const success = item.metrics?.success_count || 0;
                        const failed = item.metrics?.failed_count || 0;
                        const skipped = item.metrics?.skipped_count || 0;

                        // If still processing
                        if (item.status === 'PROCESSING') {
                          return { label: '⏳ Processing', color: 'bg-blue-100 text-blue-700' };
                        }

                        // If no records processed
                        if (total === 0) {
                          return { label: 'No Records', color: 'bg-gray-100 text-gray-700' };
                        }

                        // If all records succeeded (success equals total)
                        if (success === total && total > 0) {
                          return { label: '✅ Complete', color: 'bg-green-100 text-green-700' };
                        }

                        // If all records failed (failed equals total)
                        if (failed === total && total > 0) {
                          return { label: '❌ Failed', color: 'bg-red-100 text-red-700' };
                        }

                        // If there's a mix of success and failed (partially completed)
                        if (success > 0 && (failed > 0 || skipped > 0)) {
                          return { label: '⚠️ Partially Completed', color: 'bg-yellow-100 text-yellow-700' };
                        }

                        // If only skipped records
                        if (skipped === total && total > 0) {
                          return { label: '⊘ Skipped', color: 'bg-gray-100 text-gray-700' };
                        }

                        // Default fallback
                        return { label: item.status, color: 'bg-gray-100 text-gray-700' };
                      };

                      const fileStatus = getFileProcessedStatus(item);

                      const formatDate = (dateStr: string) => {
                        if (!dateStr) return 'N/A';
                        const date = new Date(dateStr);
                        const now = new Date();
                        const diffTime = Math.abs(now.getTime() - date.getTime());
                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays === 0) {
                          const hours = date.getHours();
                          const minutes = date.getMinutes().toString().padStart(2, '0');
                          const ampm = hours >= 12 ? 'PM' : 'AM';
                          const hour12 = hours % 12 || 12;
                          return `Today at ${hour12}:${minutes} ${ampm}`;
                        } else if (diffDays === 1) {
                          return 'Yesterday';
                        } else if (diffDays < 7) {
                          return `${diffDays} days ago`;
                        } else {
                          return date.toLocaleDateString();
                        }
                      };

                      return (
                        <tr key={item.import_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Upload className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">{item.original_filename}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${fileStatus.color}`}>
                              {fileStatus.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{item.metrics?.total_records || 0}</td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-green-600 font-medium">{item.metrics?.success_count || 0}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-red-600 font-medium">{item.metrics?.failed_count || 0}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-yellow-600 font-medium">{item.metrics?.skipped_count || 0}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(item.initiated_at)}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleViewRecordDetails(item)}
                              disabled={item.status === 'PROCESSING'}
                              className={`flex items-center gap-2 text-sm font-medium ${
                                item.status === 'PROCESSING'
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-blue-600 hover:text-blue-700'
                              }`}
                              title={item.status === 'PROCESSING' ? 'Processing in progress' : 'View details'}
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loadingHistory && historyTotalPages > 0 && (
              <Pagination
                currentPage={historyPage}
                totalPages={historyTotalPages}
                totalItems={historyTotal}
                itemsPerPage={historyLimit}
                onPageChange={(page) => loadImportHistory(page)}
                onItemsPerPageChange={(limit) => {
                  setHistoryLimit(limit);
                  loadImportHistory(1, limit);
                }}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50]}
              />
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t mt-6">
              <button
                onClick={() => {
                  setShowImportHistoryModal(false);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowImportHistoryModal(false);
                  setShowBulkImportModal(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                New Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Details Modal */}
      {showRecordDetailsModal && selectedHistoryItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
          <div className="bg-white rounded-xl p-6 w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Import Record Details</h2>
                <p className="text-sm text-gray-600 mt-1">
                  File: {selectedHistoryItem.original_filename} | Import ID: {selectedHistoryItem.import_id}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowRecordDetailsModal(false);
                  setImportRecords([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{selectedHistoryItem.metrics?.total_records || 0}</p>
                <p className="text-sm text-gray-600">Total Records</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{selectedHistoryItem.metrics?.success_count || 0}</p>
                <p className="text-sm text-green-600">Successful</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-700">{selectedHistoryItem.metrics?.skipped_count || 0}</p>
                <p className="text-sm text-yellow-600">Skipped</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-700">{selectedHistoryItem.metrics?.failed_count || 0}</p>
                <p className="text-sm text-red-600">Failed</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email, or employee ID..."
                    value={recordsSearch}
                    onChange={(e) => setRecordsSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && selectedHistoryItem) {
                        loadImportRecords(selectedHistoryItem.import_id, 1);
                      }
                    }}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={recordsFilter}
                  onChange={(e) => {
                    setRecordsFilter(e.target.value as any);
                    if (selectedHistoryItem) {
                      loadImportRecords(selectedHistoryItem.import_id, 1);
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ALL">All Records</option>
                  <option value="SUCCESS">Success</option>
                  <option value="FAILED">Failed</option>
                  <option value="SKIPPED">Skipped</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
              <button
                onClick={() => {
                  if (selectedHistoryItem) {
                    loadImportRecords(selectedHistoryItem.import_id, recordsPage);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>

            {/* Records Table */}
            {loadingRecords ? (
              <TableSkeleton rows={8} columns={8} showHeader={true} />
            ) : importRecords.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No records found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Groups</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {importRecords.map((record) => {
                      const getStatusColor = (status: string) => {
                        switch (status) {
                          case 'SUCCESS': return 'bg-green-100 text-green-700';
                          case 'FAILED': return 'bg-red-100 text-red-700';
                          case 'SKIPPED': return 'bg-yellow-100 text-yellow-700';
                          case 'PENDING': return 'bg-gray-100 text-gray-700';
                          default: return 'bg-gray-100 text-gray-700';
                        }
                      };

                      return (
                        <tr key={record.record_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{record.row_number}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                              {record.status === 'SUCCESS' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {record.status === 'FAILED' && <AlertCircle className="w-3 h-3 mr-1" />}
                              {record.status === 'SKIPPED' && <Clock className="w-3 h-3 mr-1" />}
                              {record.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.record_data.email}</td>

                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {record.record_data.given_name} {record.record_data.family_name}
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.record_data.mobile_number || '-'}</td>

                          <td className="px-4 py-3 text-sm text-gray-900">{record.record_data.employee_id || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="w-48 truncate" title={record.record_data.groups || '-'}>
                              {record.record_data.groups || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {record.error_message ? (
                              <div className="text-sm text-red-600 w-48">
                                <p className="truncate" title={record.error_message}>
                                  {record.error_message}
                                </p>
                                {record.error_details && (
                                  <button
                                    onClick={() => {
                                      alert(JSON.stringify(record.error_details, null, 2));
                                    }}
                                    className="text-xs text-red-500 hover:underline mt-1"
                                  >
                                    View Details
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {recordsTotalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-gray-700">
                  Showing {((recordsPage - 1) * recordsLimit) + 1} to {Math.min(recordsPage * recordsLimit, recordsTotal)} of {recordsTotal} records
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (recordsPage > 1 && selectedHistoryItem) {
                        loadImportRecords(selectedHistoryItem.import_id, recordsPage - 1);
                      }
                    }}
                    disabled={recordsPage === 1}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {recordsPage} of {recordsTotalPages}
                  </span>
                  <button
                    onClick={() => {
                      if (recordsPage < recordsTotalPages && selectedHistoryItem) {
                        loadImportRecords(selectedHistoryItem.import_id, recordsPage + 1);
                      }
                    }}
                    disabled={recordsPage === recordsTotalPages}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t mt-6">
              <button
                onClick={() => {
                  setShowRecordDetailsModal(false);
                  setImportRecords([]);
                  setShowImportHistoryModal(true);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to History
              </button>
              <button
                onClick={() => {
                  setShowRecordDetailsModal(false);
                  setImportRecords([]);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 common-modal-overlay">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Lock className="w-5 h-5 flex-shrink-0" />
                  Reset Password
                </h2>
                <p className="text-sm text-gray-600 mt-1 break-all">
                  Reset password for {resetPasswordUser.email}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setResetPasswordUser(null);
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError(null);
                }}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Employee Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                  {resetPasswordUser.given_name[0]}{resetPasswordUser.family_name[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">
                    {resetPasswordUser.given_name} {resetPasswordUser.family_name}
                  </p>
                  <p className="text-sm text-gray-600 break-all">{resetPasswordUser.email}</p>
                </div>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-800 mb-2">Password Requirements:</p>
              <ul className="text-xs text-blue-700 space-y-1">
                <li className="flex items-center gap-1">
                  <span className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                    • At least 8 characters
                  </span>
                </li>
                <li className="flex items-center gap-1">
                  <span className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                    • One uppercase letter
                  </span>
                </li>
                <li className="flex items-center gap-1">
                  <span className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>
                    • One lowercase letter
                  </span>
                </li>
                <li className="flex items-center gap-1">
                  <span className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                    • One number
                  </span>
                </li>
                <li className="flex items-center gap-1">
                  <span className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-600' : ''}>
                    • One special character
                  </span>
                </li>
              </ul>
            </div>

            {/* Password Fields */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showResetPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordError(null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showResetPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showResetPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError(null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showResetPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {passwordError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {passwordError}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setResetPasswordUser(null);
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError(null);
                }}
                disabled={resettingPassword}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={performPasswordReset}
                disabled={!newPassword || !confirmPassword || resettingPassword}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resettingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Resetting...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4" />
                    Reset Password
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Employee"
        description="Are you sure you want to delete this employee? This action will permanently remove the employee and cannot be undone."
        itemName={userToDelete?.name}
        isLoading={loading}
      />

      {/* User Form Modal - Unified for Add/Edit */}
      <UserFormModal
        isOpen={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          setEditingUser(null);
        }}
        onSuccess={() => {
          loadUsers();
          loadMetrics();
        }}
        editingUser={editingUser}
        availableGroups={availableGroups as any}
      />
    </div>
  );
}
